/**
 * Install state — load-bearing, not a growth tactic (SPEC.md §10.1).
 *
 * Safari evicts script-writable storage after 7 days of non-use. That is the
 * session AND unsynced expenses in IndexedDB. **Installed PWAs are exempt.** So
 * a user who stays in a browser tab will be signed out, and can lose data, after
 * a week idle — which is why onboarding walks everyone through installing.
 */

import { useCallback, useEffect, useState } from "react";

import { bridge } from "@/native/impl";

export type Platform = "ios" | "android" | "desktop";

export interface InstallState {
  /** Running as an installed app rather than in a browser tab. */
  isInstalled: boolean;
  platform: Platform;
  /** Android/desktop Chrome captured a prompt we can trigger directly. */
  canPrompt: boolean;
  /** Fire the native install prompt. Resolves true if the user accepted. */
  prompt: () => Promise<boolean>;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as a Mac; the touch-point check disambiguates it.
  const isIos = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIos) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

function detectInstalled(): boolean {
  // A native app is installed by definition, and its storage is not subject to
  // Safari's 7-day eviction — so the install gate is a web-only step
  // (SPEC §10.1).
  if (bridge.platform !== "web") return true;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari's own, non-standard signal — it does not implement display-mode
  // reliably for home-screen apps.
  return "standalone" in navigator && navigator.standalone === true;
}

declare global {
  interface Navigator {
    standalone?: boolean;
  }
}

export function useInstallState(): InstallState {
  const [isInstalled, setIsInstalled] = useState(detectInstalled);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const platform = detectPlatform();

  useEffect(() => {
    const onBeforePrompt = (event: Event): void => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = (): void => {
      setIsInstalled(true);
      setDeferred(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforePrompt);
    window.addEventListener("appinstalled", onInstalled);

    const media = window.matchMedia("(display-mode: standalone)");
    const onDisplayChange = (): void => setIsInstalled(detectInstalled());
    media.addEventListener("change", onDisplayChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforePrompt);
      window.removeEventListener("appinstalled", onInstalled);
      media.removeEventListener("change", onDisplayChange);
    };
  }, []);

  const prompt = useCallback(async () => {
    if (!deferred) return false;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    return outcome === "accepted";
  }, [deferred]);

  return { isInstalled, platform, canPrompt: deferred !== null, prompt };
}

/**
 * Platform-correct install instructions.
 *
 * iOS has no programmatic install and no share-target, so the only route is
 * Safari's Share menu — which means telling the user exactly where to tap.
 */
export function installInstructions(platform: Platform): { title: string; steps: string[] } {
  switch (platform) {
    case "ios":
      return {
        title: "Add Tide Tally to your Home Screen",
        steps: [
          "Tap the Share button at the bottom of Safari",
          "Scroll down and tap “Add to Home Screen”",
          "Tap “Add” — then open Tide Tally from your Home Screen",
        ],
      };
    case "android":
      return {
        title: "Install Tide Tally",
        steps: [
          "Tap Install below, or open the browser menu",
          "Choose “Install app” or “Add to Home screen”",
          "Open Tide Tally from your app drawer",
        ],
      };
    case "desktop":
      return {
        title: "Install Tide Tally",
        steps: [
          "Click Install below, or the install icon in the address bar",
          "Tide Tally opens in its own window from then on",
        ],
      };
  }
}
